import React from 'react';
import { useTranslation } from 'react-i18next';
import { BigGrid } from './BigGrid';
import { PointInspector } from './PointInspector';
import './PointMode.css';

export interface PointModePest {
  id: string;
  code: string;
  name: string;
  actionThreshold: number;
}

export interface PointModeProps {
  incPests: PointModePest[];
  activePestId: string;
  gridMode: 'solo' | 'all';
  currentPoint: number;
  cellState: (point: number, pestId: string) => 'empty' | 'clean' | 'infested';
  pctFor: (pestId: string) => number;
  isOver: (pestId: string) => boolean;
  photos: Set<number>;
  /** Optional: returns true if point i is infested by any pest (for All mode faint cells) */
  allInfestedAt?: (i: number) => boolean;
  onSelectPest: (id: string) => void;
  onSetGridMode: (mode: 'solo' | 'all') => void;
  onPickPoint: (point: number) => void;
  onSetVerdict: (point: number, pestId: string, verdict: 'clean' | 'infested') => void;
  onMarkClean?: (point: number) => void;
  onPrev: () => void;
  onNext: () => void;
  onNextEmpty: () => void;
  onAttachPhoto: (file: File) => void;
}

export function PointMode({
  incPests,
  activePestId,
  gridMode,
  currentPoint,
  cellState,
  pctFor,
  isOver,
  photos,
  allInfestedAt,
  onSelectPest,
  onSetGridMode,
  onPickPoint,
  onSetVerdict,
  onMarkClean,
  onPrev,
  onNext,
  onNextEmpty,
  onAttachPhoto,
}: PointModeProps) {
  const { t } = useTranslation();

  const activePest = incPests.find(p => p.id === activePestId);
  const activePct = pctFor(activePestId);
  const activeOver = isOver(activePestId);

  return (
    <div className="point-mode">
      {/* LEFT — Grid card */}
      <div className="card card-pad">
        {/* Pest rail */}
        <div className="pestrail">
          {incPests.map(pest => {
            const pct = pctFor(pest.id);
            const over = isOver(pest.id);
            const selected = pest.id === activePestId;
            return (
              <button
                key={pest.id}
                data-testid={`rail-${pest.id}`}
                className={`pchip${selected ? ' pchip--selected' : ''}${over ? ' pchip--over' : ''}`}
                onClick={() => onSelectPest(pest.id)}
                style={{ background: 'none', font: 'inherit' }}
              >
                <span className="pchip__code">{pest.code}</span>
                {over && (
                  <span className="pchip__flag" aria-hidden="true">!</span>
                )}
                <span className="pchip__pct">{pct}%</span>
              </button>
            );
          })}
        </div>

        {/* Grid header */}
        <div className="gridhead">
          <span className={`gridhead__name${activeOver ? ' gridhead__name--over' : ''}`}>
            {activePest?.code ?? activePestId}
          </span>
          <span className="gridhead__hint">
            {activePest?.name ?? ''} · {t('fieldScout.desktop.point.actionHint', { action: activePest?.actionThreshold ?? 0 })}
          </span>
          <span className={`gridhead__pct${activeOver ? ' gridhead__pct--over' : ''}`}>
            {activePct}%
          </span>
        </div>

        {/* Solo / All toggle */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <span className="modes">
            <button
              className={`modes__btn${gridMode === 'solo' ? ' modes__btn--on' : ''}`}
              onClick={() => onSetGridMode('solo')}
            >
              {t('fieldScout.desktop.point.solo')}
            </button>
            <button
              className={`modes__btn${gridMode === 'all' ? ' modes__btn--on' : ''}`}
              onClick={() => onSetGridMode('all')}
            >
              {t('fieldScout.desktop.point.all')}
            </button>
          </span>
        </div>

        {/* Big Grid */}
        <BigGrid
          activePestId={activePestId}
          cellState={cellState}
          currentPoint={currentPoint}
          photos={photos}
          gridMode={gridMode}
          allInfestedAt={allInfestedAt}
          onPickPoint={onPickPoint}
        />

        {/* Legend */}
        <div className="point-legend">
          <span>
            <i className="point-legend__swatch" style={{ background: 'var(--red-500)' }} />
            {t('fieldScout.desktop.legend.infested')}
          </span>
          <span>
            <i className="point-legend__swatch" style={{ background: 'var(--green-200)' }} />
            {t('fieldScout.desktop.legend.clean')}
          </span>
          <span>
            <i className="point-legend__swatch" style={{ background: 'var(--neutral-200)' }} />
            {t('fieldScout.desktop.legend.notInspected')}
          </span>
        </div>
      </div>

      {/* RIGHT — Inspector card */}
      <div className="card card-pad">
        <PointInspector
          incPests={incPests}
          currentPoint={currentPoint}
          cellState={cellState}
          onSetVerdict={onSetVerdict}
          onMarkClean={onMarkClean}
          onAttachPhoto={onAttachPhoto}
          onPrev={onPrev}
          onNext={onNext}
          onNextEmpty={onNextEmpty}
        />
      </div>
    </div>
  );
}

export default PointMode;
